import { Injectable } from '@angular/core';
import { getValueByKey } from '@awp/core/utils';
import { Observable, Subject } from 'rxjs';

export type ContextType = { [key: string]: string | number | Date | any };

export abstract class ContentParser {
  /**
   * Interpolates a string to replace parameters
   * "This is a {{ key }}" ==> "This is a value", with params = { key: "value" }
   * @param expr
   * @param params
   */
  abstract interpolate(expr: string | Function, params?: any): string;

  /**
   * Gets a value from an object by composed key
   * parser.getValue({ key1: { keyA: 'valueI' }}, 'key1.keyA') ==> 'valueI'
   * @param target
   * @param key
   */
  abstract getValue(target: any, key: string): any;

  abstract context: ContextType;

  contextKey: string = 'context';

  contextChanged: Observable<void>;
}

@Injectable()
export class ContentDefaultParser extends ContentParser {

  templateMatcher: RegExp = /{{\s?([^{}\s]*)\s?}}/g;

  private _context: ContextType;
  get context(): ContextType {
    return this._context;
  }
  set context(value: ContextType) {
    this._context = value;
    this._contextChanged.next();
  }

  private _contextChanged = new Subject<void>();
  contextChanged = this._contextChanged.asObservable();

  public interpolate(expr: string | Function, params?: any): string {
    let result: string;

    if (typeof expr === 'string') {
      result = this.interpolateString(expr, params);
    } else if (typeof expr === 'function') {
      result = this.interpolateFunction(expr, params);
    } else {
      // this should not happen, but an unrelated TranslateService test depends on it
      result = expr as string;
    }

    // Fix issue with Umbraco encoding for href
    if (result != null) {
      result = result.replace(/href="\/http/g, 'href="http');
    }

    return result;
  }

  getValue(target: any, key: string): any {
    if (key.startsWith(`${this.contextKey}.`)) {
      target = this.context;
      key = key.substr(`${this.contextKey}.`.length);
    }

    target = getValueByKey(target, key);
    return target;
  }

  private interpolateFunction(fn: Function, params?: any) {
    return fn(params);
  }

  private interpolateString(expr: string, params?: any) {
    if ((params == null && this.context == null) || !expr.includes('{{')) {
      return expr;
    }

    let result = expr.replace(this.templateMatcher, (substring: string, token: string) => {
      let value = this.getValue(params, token) || '';
      // If the value contains {{ value }} then this needs to be interpolated as well.
      while (typeof value === 'string' && value.includes('{{')) {
        value = this.interpolateString(value, params);
      }
      return value;
    });

    return result;
  }
}
